import type { ReactNode } from "react";
import { useMemo, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { SvgIcon } from "@mui/material";
import HomeSmileIcon from "src/icons/untitled-ui/duocolor/home-smile";
import MonitorHeartIcon from "@mui/icons-material/MonitorHeart";
import Users03Icon from "src/icons/untitled-ui/duocolor/users-03";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import VerifiedUserIcon from "@mui/icons-material/VerifiedUser";
import Diversity3Icon from "@mui/icons-material/Diversity3";
import PersonIcon from "@mui/icons-material/Person";
// import UploadFileIcon from "@mui/icons-material/UploadFile";
import { tokens } from "src/locales/tokens";
import { paths } from "src/paths";
import CompareArrowsIcon from "@mui/icons-material/CompareArrows";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import CurrencyExchangeIcon from "@mui/icons-material/CurrencyExchange";
import ContactlessIcon from "@mui/icons-material/Contactless";
import SendIcon from "@mui/icons-material/Send";
import TuneIcon from "@mui/icons-material/Tune";
import BusinessIcon from "@mui/icons-material/Business";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import SubscriptionsIcon from "@mui/icons-material/Subscriptions";

export interface Item {
  disabled?: boolean;
  external?: boolean;
  icon?: ReactNode;
  items?: Item[];
  label?: ReactNode;
  path?: string | { index: string; details: string; edit: string };
  title: string;
  hasToggle?: boolean;
  isDarkMode?: boolean;
  onToggle?: () => void;
}

export interface Section {
  items: Item[];
  subheader?: string;
  hasDividerBelow?: boolean;
}

export const useSections = (): Section[] => {
  const { t } = useTranslation();

  const [mode, setMode] = useState<"light" | "dark">("light");

  const setLightMode = useCallback(() => {
    setMode("light");
    document.body.style.backgroundColor = "#fff";
    document.body.style.color = "#000";
  }, []);

  const setDarkMode = useCallback(() => {
    setMode("dark");
    document.body.style.backgroundColor = "#121212";
    document.body.style.color = "#fff";
  }, []);

  return useMemo<Section[]>(
    () => [
      // Overview
      {
        items: [
          {
            title: t(tokens.nav.overview),
            path: paths.dashboard.index,
            icon: (
              <SvgIcon fontSize="small">
                <HomeSmileIcon />
              </SvgIcon>
            ),
          },
          {
            title: "Clients",
            path: paths.dashboard.customers.index,
            icon: (
              <SvgIcon fontSize="small">
                <Users03Icon />
              </SvgIcon>
            ),
          },
        ],
      },

      // Remittance product
      {
        subheader: "Remittance",
        items: [
          {
            title: "Remittance",
            icon: (
              <SvgIcon fontSize="small">
                <SendIcon />
              </SvgIcon>
            ),
            items: [
              {
                title: "Users",
                path: paths.dashboard.users,
                icon: (
                  <SvgIcon fontSize="small">
                    <Users03Icon />
                  </SvgIcon>
                ),
              },
              // {
              //   title: "Transactions",
              //   path: paths.dashboard.transactions.index,
              //   icon: (
              //     <SvgIcon fontSize="small">
              //       <AttachMoneyIcon />
              //     </SvgIcon>
              //   ),
              // },
              {
                title: "Transactions",
                path: paths.dashboard.paywizePayouts,
                icon: (
                  <SvgIcon fontSize="small">
                    <AttachMoneyIcon />
                  </SvgIcon>
                ),
              },
              {
                title: "Payment Gateways",
                path: paths.dashboard.paymentGateway,
                icon: (
                  <SvgIcon fontSize="small">
                    <AccountBalanceIcon />
                  </SvgIcon>
                ),
              },
            ],
          },
        ],
      },

      // OTC Prime product
      {
        subheader: "OTC Prime",
        items: [
          {
            title: "OTC Prime",
            icon: (
              <SvgIcon fontSize="small">
                <CurrencyExchangeIcon />
              </SvgIcon>
            ),
            items: [
              {
                title: "Transactions",
                path: "/dashboard/otc",
                icon: (
                  <SvgIcon fontSize="small">
                    <CompareArrowsIcon />
                  </SvgIcon>
                ),
              },
              {
                title: "Trade",
                path: "/dashboard/otcTrade",
                icon: (
                  <SvgIcon fontSize="small">
                    <SwapHorizIcon />
                  </SvgIcon>
                ),
              },
              {
                title: "Wallet Balance",
                path: "/dashboard/otcWallet",
                icon: (
                  <SvgIcon fontSize="small">
                    <AccountBalanceWalletIcon />
                  </SvgIcon>
                ),
              },
              {
                title: "Client All Whitelisted Bank/Address",
                path: "/dashboard/clientWhitelisted",
                icon: (
                  <SvgIcon fontSize="small">
                    <ContactlessIcon />
                  </SvgIcon>
                ),
              },
              {
                title: "Configuration",
                path: "/dashboard/config",
                icon: (
                  <SvgIcon fontSize="small">
                    <TuneIcon />
                  </SvgIcon>
                ),
              },
            ],
          },
        ],
      },

      // Company / Admin
      {
        subheader: "Msc",
        items: [
          {
            title: "Company",
            icon: (
              <SvgIcon fontSize="small">
                <BusinessIcon />
              </SvgIcon>
            ),
            items: [
              {
                title: "Team",
                path: paths.dashboard.team.index,
                icon: (
                  <SvgIcon fontSize="small">
                    <Diversity3Icon />
                  </SvgIcon>
                ),
              },
              {
                title: "System Status",
                path: paths.dashboard.systemStatus,
                icon: (
                  <SvgIcon fontSize="small">
                    <MonitorHeartIcon />
                  </SvgIcon>
                ),
              },
              // {
              //   title: "CSV Upload",
              //   path: paths.dashboard.csvUpload,
              //   icon: (
              //     <SvgIcon fontSize="small">
              //       <UploadFileIcon />
              //     </SvgIcon>
              //   ),
              // },
              {
                title: "Verify KYC",
                path: paths.dashboard.verifyKyc,
                icon: (
                  <SvgIcon fontSize="small">
                    <VerifiedUserIcon />
                  </SvgIcon>
                ),
              },
              {
                title: "Subscribers",
                path: paths.dashboard.subscribers,
                icon: (
                  <SvgIcon fontSize="small">
                    <SubscriptionsIcon />
                  </SvgIcon>
                ),
              },
              // {
              //   title: "DB ENV",
              //   icon: (
              //     <SvgIcon fontSize="small">
              //       <TuneIcon />
              //     </SvgIcon>
              //   ),
              //   items: [
              //     { title: "Production", path: "production" },
              //     { title: "Deploy", path: "deploy" },
              //   ],
              // },
            ],
          },
        ],
      },

      // DB ENV
      // {
      //   items: [
      //     {
      //       title: "DB ENV",
      //       icon: (
      //         <SvgIcon fontSize="small">
      //           <TuneIcon />
      //         </SvgIcon>
      //       ),
      //       items: [
      //         { title: "Production", path: "production" },
      //         { title: "Deploy", path: "deploy" },
      //       ],
      //     },
      //   ],
      // },

      // Profile
      {
        items: [
          {
            title: "Profile",
            path: paths.dashboard.profile,
            icon: (
              <SvgIcon fontSize="small" sx={{ color: "#ff6b00" }}>
                <PersonIcon />
              </SvgIcon>
            ),
          },
        ],
      },
    ],
    [t, mode, setLightMode, setDarkMode]
  );
};
